import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ChatStore } from '../store';
import { BotApiResponse, BotMessage, ChatRequest, SessionStartRequest, UserMessage } from '../interfaces';
import { environment } from '../../../../environments/environment';
import { BotConfigService } from './bot-config.service';

@Injectable({
  providedIn: 'root',
})
export class ChatEngineService {
  private readonly chatStore = inject(ChatStore);
  private readonly http = inject(HttpClient);
  private readonly botConfigService = inject(BotConfigService);
  private readonly sessionEndpoint = `${environment.chatApiUrl}/api/v1/session/start`;
  private readonly chatEndpoint = `${environment.chatApiUrl}/api/v1/chat`;

  public initializeConversation(): void {
    this.chatStore.setMessages([]);
    this.chatStore.setLoading(true);

    const generation = this.chatStore.conversationGeneration();

    this.botConfigService.whenReady().then(() => {
      if (this.isStale(generation)) return;

      const config = this.botConfigService.config();
      const body: SessionStartRequest = {
        non_alcohol: config?.non_alcoholic,
        account_email: config?.account_email,
        delivery_charge: config?.delivery_charge,
        discount_percentage: config?.discount_percentage,
        tip_percentage: config?.tip_percentage,
        service_charge_percentage: config?.service_charge_percentage,
        shipping_charge: config?.shipping_charge,
      };

      this.http.post<BotApiResponse>(this.sessionEndpoint, body).subscribe({
        next: (response) => {
          if (this.isStale(generation)) return;
          if (response.success && response.data) {
            this.chatStore.setSessionId(response.data.session_id);
            const greeting: BotMessage = {
              id: crypto.randomUUID(),
              type: 'bot',
              timestamp: response.data.timestamp,
              payload: response.data.payload,
              metadata: { session_id: response.data.session_id, debug: response.data.debug },
            };
            this.chatStore.addMessage(greeting);
          }
          this.chatStore.setLoading(false);
        },
        error: () => {
          if (this.isStale(generation)) return;
          this.chatStore.addMessage(this.errorMessage());
          this.chatStore.setLoading(false);
        },
      });
    });
  }

  public sendMessage(message: string, sourceMessageId?: string): void {
    if (sourceMessageId) {
      this.chatStore.disableMessage(sourceMessageId);
    }

    const userMessage: UserMessage = {
      id: crypto.randomUUID(),
      type: 'user',
      text: message,
      timestamp: new Date().toISOString(),
    };

    this.chatStore.addMessage(userMessage);
    this.chatStore.setLoading(true);

    const generation = this.chatStore.conversationGeneration();
    const body: ChatRequest = {
      session_id: this.chatStore.sessionId() ?? undefined,
      user_message: message,
    };

    this.http.post<BotApiResponse>(this.chatEndpoint, body).subscribe({
      next: (response) => {
        if (this.isStale(generation)) return;
        if (response.success && response.data) {
          const botMessage: BotMessage = {
            id: crypto.randomUUID(),
            type: 'bot',
            timestamp: response.data.timestamp,
            payload: response.data.payload,
            metadata: { session_id: response.data.session_id, debug: response.data.debug },
          };
          this.chatStore.addMessage(botMessage);
        }
        this.chatStore.setLoading(false);
      },
      error: () => {
        if (this.isStale(generation)) return;
        this.chatStore.addMessage(this.errorMessage());
        this.chatStore.setLoading(false);
      },
    });
  }

  private isStale(generation: number): boolean {
    return generation !== this.chatStore.conversationGeneration();
  }

  private errorMessage(): BotMessage {
    return {
      id: crypto.randomUUID(),
      type: 'bot',
      timestamp: new Date().toISOString(),
      payload: [{ type: 'text', data: { text: 'Something went wrong. Please try again.' } }],
    };
  }
}
